import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  AGE_GROUPS,
  POSTS,
  TOPICS,
  searchPosts,
  type AgeGroupId,
  type TopicId,
} from "@/lib/montessori-koulu-posts";
import { ArticleCard } from "@/components/hub/ArticleCard";

type Filter = "kaikki" | TopicId;

const CHIPS: ReadonlyArray<{ id: Filter; label: string }> = [
  { id: "kaikki", label: "Kaikki" },
  ...TOPICS.map((t) => ({ id: t.id as Filter, label: t.label })),
];

/**
 * Search + chip filtering for the hub archive. Everything runs on the
 * already-loaded article registry, so there is no network work and no
 * ecommerce code in this section.
 */
export function HubExplorer({ initialAge }: { initialAge?: AgeGroupId }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("kaikki");
  const [age, setAge] = useState<AgeGroupId | undefined>(initialAge);

  const results = useMemo(() => {
    let list = searchPosts(query);
    if (filter !== "kaikki") list = list.filter((p) => p.topic === filter);
    if (age) list = list.filter((p) => p.ageGroups?.includes(age));
    return list;
  }, [query, filter, age]);

  return (
    <div>
      <div className="flex flex-col gap-5">
        <label className="block max-w-xl">
          <span className="font-display text-2xl text-foreground md:text-3xl">Mistä haluaisit oppia?</span>
          <span className="relative mt-4 flex items-center">
            <Search className="pointer-events-none absolute left-4 h-4 w-4 text-muted-foreground" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Esim. Pikler, oppimistorni, 2-vuotias, itsenäisyys…"
              className="w-full rounded-full border border-border bg-card py-3.5 pl-11 pr-4 text-base text-foreground placeholder:text-muted-foreground focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/30"
            />
          </span>
        </label>

        <div
          role="group"
          aria-label="Aihesuodattimet"
          className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1 md:mx-0 md:flex-wrap md:px-0"
        >
          {CHIPS.map((c) => {
            const active = filter === c.id;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(c.id)}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${
                  active
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card text-foreground hover:border-sage hover:text-sage"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        <div role="group" aria-label="Ikävaihe" className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1 md:mx-0 md:flex-wrap md:px-0">
          {AGE_GROUPS.map((g) => {
            const active = age === g.id;
            return (
              <button
                key={g.id}
                type="button"
                aria-pressed={active}
                onClick={() => setAge(active ? undefined : g.id)}
                className={`shrink-0 rounded-full border px-4 py-1.5 text-xs uppercase tracking-[0.16em] transition-colors ${
                  active ? "border-sage bg-sage/15 text-sage" : "border-border bg-background text-muted-foreground hover:text-foreground"
                }`}
              >
                {g.label}
              </button>
            );
          })}
        </div>
      </div>

      <p aria-live="polite" className="mt-6 text-sm text-muted-foreground">
        {results.length} {results.length === 1 ? "opas" : "opasta"}
        {results.length !== POSTS.length ? " valituilla rajauksilla" : ""}
      </p>

      {results.length > 0 ? (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((p) => (
            <ArticleCard key={p.to} p={p} titleAs="h3" />
          ))}
        </div>
      ) : (
        <p className="mt-6 rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Emme löytäneet hakuasi vastaavaa opasta. Kokeile lyhyempää hakusanaa, esimerkiksi ”Pikler” tai ”eteinen”.
        </p>
      )}
    </div>
  );
}